var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment, useState } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Button, Loader, Menu } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { DocumentMetadataPropertyTypeEnum } from 'shared/domains/apollo/generated/types';
import { useFuse } from 'shared/domains/common-utils/useFuse';
import { BooleanMenu } from './BooleanMenu';
import { DateMenu } from './DateMenu';
import { ListMenu } from './ListMenu';
import { NoResults } from './NoResults';
import { NumberMenu } from './NumberMenu';
import { ProcessingBulkUpdateDialog } from './ProcessingBulkUpdateDialog';
import { UserMenu } from './UserMenu';
import { getMetadataProperties } from './getMetadataProperties';
import { getMetadataPropertyInitialValue } from './getMetadataPropertyInitialValue';
import { useBulkUpdateMetadataProperty } from './useBulkUpdateMetadataProperty';
import { useTableContext } from '../../SearchResultsTable/TableContext';
const BULK_ACTIONS_LIMIT = 100;
export function BulkSetMetadata() {
    const { selected } = useTableContext();
    const documentIds = selected.map((item) => item.entityId);
    const [query, setQuery] = useState('');
    const { allProperties, propertyGroups } = getMetadataProperties(query);
    const { result, search, active } = useFuse(allProperties, { keys: ['label'], minMatchCharLength: 2 });
    const noResults = active && result.length === 0;
    const activeSearch = active && query.length >= 2;
    const [menuOpen, setMenuOpen] = useState(false);
    const hideMenu = () => {
        setMenuOpen(false);
        setQuery('');
    };
    const toggleMenu = () => setMenuOpen(!menuOpen);
    const { pollingState, setMetadataProperty, clearMetadataProperty, loading } = useBulkUpdateMetadataProperty();
    const showLoader = loading || pollingState.state === 'polling';
    const buttonDisabled = selected.length > BULK_ACTIONS_LIMIT || showLoader;
    const setMetadata = (property, value) => __awaiter(this, void 0, void 0, function* () {
        setMetadataProperty({ documentIds, property, value });
        hideMenu();
    });
    const clearMetadata = (property) => __awaiter(this, void 0, void 0, function* () {
        clearMetadataProperty({ documentIds, property });
        hideMenu();
    });
    const handleSearch = (value) => {
        setQuery(value);
        search(value);
    };
    const renderPropertyMenu = (property) => {
        const { type, name } = property;
        if (type === DocumentMetadataPropertyTypeEnum.Number) {
            return (<NumberMenu key={property.name} property={property} onChange={setMetadata} onClear={clearMetadata} initialValue={getMetadataPropertyInitialValue(selected, property)}/>);
        }
        if (type === DocumentMetadataPropertyTypeEnum.Boolean) {
            return (<BooleanMenu key={property.name} property={property} onChange={setMetadata} onClear={clearMetadata} initialValue={getMetadataPropertyInitialValue(selected, property)}/>);
        }
        if (type === DocumentMetadataPropertyTypeEnum.Date) {
            return (<DateMenu key={property.name} property={property} onChange={setMetadata} onClear={clearMetadata} initialValue={getMetadataPropertyInitialValue(selected, property)}/>);
        }
        if (type === DocumentMetadataPropertyTypeEnum.List) {
            return (<ListMenu key={property.name} property={property} onChange={setMetadata} onClear={clearMetadata} initialValue={getMetadataPropertyInitialValue(selected, property)}/>);
        }
        if (type === DocumentMetadataPropertyTypeEnum.Reference && name === 'ContractOwner') {
            return (<UserMenu key={property.name} property={property} onChange={setMetadata} onClear={clearMetadata} initialValue={getMetadataPropertyInitialValue(selected, property)}/>);
        }
        return null;
    };
    const renderPropertyGroup = (group) => {
        const { type } = group;
        if (type === 'nested') {
            return group.children.map(renderPropertyMenu);
        }
        if (type === 'label') {
            return group.children.map((labelProperty, index) => (<Fragment key={labelProperty.id}>
          {index > 0 && <Menu.Divider />}
          <Menu.Label>{labelProperty.label}</Menu.Label>
          {labelProperty.children.map(renderPropertyMenu)}
        </Fragment>));
        }
        return null;
    };
    return (<Fragment>
      {pollingState.state === 'polling' && <ProcessingBulkUpdateDialog {...pollingState}/>}

      <Menu.Root side="top" open={menuOpen} maxWidth="unset" onClose={hideMenu} target={<Button size="small" appearance="glass" disabled={buttonDisabled} onClick={toggleMenu}>
            {showLoader ? <Loader /> : <Falcon icon="tag"/>}
            <Trans comment="Bulk action: Metadata">Metadata</Trans>
          </Button>}>
        <Menu.HeaderSearch value={query} onChange={handleSearch} placeholder={t({
            message: 'Find metadata property...',
            comment: 'Bulk action: Metadata search input placeholder',
        })}/>

        {activeSearch
            ? result.map(renderPropertyMenu)
            : propertyGroups.map((nestedProperty) => (<Menu.ItemNested key={nestedProperty.id} target={nestedProperty.label}>
                {renderPropertyGroup(nestedProperty.group)}
              </Menu.ItemNested>))}

        {noResults && (<NoResults>
            <Trans comment="Bulk action: Metadata - No properties found">No properties found</Trans>
          </NoResults>)}
      </Menu.Root>
    </Fragment>);
}
